import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { FiEye, FiPlay, FiCheckCircle, FiSearch, FiZap, FiFileText } from 'react-icons/fi';
import api from '../../api';
import { extractError } from '../../api/client';
import { toast, confirmDialog, showError } from '../../utils/alert';
import PageHeader from '../../components/common/PageHeader';
import DataTable from '../../components/common/DataTable';
import Pagination from '../../components/common/Pagination';
import Modal from '../../components/common/Modal';
import StatusBadge from '../../components/common/StatusBadge';
import { useAuth } from '../../context/AuthContext';
import { formatMoney, currentMonth } from '../../utils/format';

export default function Payroll() {
  const { hasPermission } = useAuth();
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState(null);
  const [params, setParams] = useState({ page: 1, search: '', month: currentMonth(), per_page: 100 });
  const [loading, setLoading] = useState(false);
  const [previewModal, setPreviewModal] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [generateModal, setGenerateModal] = useState(false);
  const [processModal, setProcessModal] = useState(false);
  const [activePayroll, setActivePayroll] = useState(null);
  const [errors, setErrors] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.payroll.list(params);
      const list = Array.isArray(data.data) ? data.data : data.payrolls || data;
      setRows(Array.isArray(list) ? list : list.data || []);
      setMeta(data.data ? { current_page: data.current_page, last_page: data.last_page, total: data.total, per_page: data.per_page, per_page_options: data.data.per_page_options } : null);
    } catch (err) {
      showError('Failed to load payroll', extractError(err));
    } finally {
      setLoading(false);
    }
  }, [params]);

  useEffect(() => {
    load();
  }, [load]);

  const preview = async () => {
    const errs = {};
    if (!params.month) errs.month = 'Month is required';
    if (Object.keys(errs).length) {
      setErrors(errs);
      return;
    }
    setPreviewLoading(true);
    try {
      const { data } = await api.payroll.preview({ month: params.month });
      setPreviewData(data);
      setPreviewModal(true);
    } catch (err) {
      showError('Failed to preview payroll', extractError(err));
    } finally {
      setPreviewLoading(false);
    }
  };

  const generate = async () => {
    const errs = {};
    if (!params.month) errs.month = 'Month is required';
    if (Object.keys(errs).length) {
      setErrors(errs);
      return;
    }
    const res = await confirmDialog({
      title: 'Generate payroll?',
      text: `This will create draft payrolls for all active employees for ${params.month}.`,
      confirmText: 'Yes, generate',
    });
    if (!res.isConfirmed) return;
    try {
      await api.payroll.generate({ month: params.month });
      toast('success', '', 'Payroll drafts generated');
      setGenerateModal(false);
      load();
    } catch (err) {
      showError('Failed to generate payroll', extractError(err));
    }
  };

  const approve = async (row) => {
    const res = await confirmDialog({ title: 'Approve payroll?', confirmText: 'Yes, approve' });
    if (!res.isConfirmed) return;
    try {
      await api.payroll.approve(row.id);
      toast('success', '', 'Payroll approved');
      load();
    } catch (err) {
      showError('Failed to approve', extractError(err));
    }
  };

  const openProcess = (row) => {
    setActivePayroll(row);
    setProcessModal(true);
  };

  const process = async () => {
    try {
      await api.payroll.process(activePayroll.id, {});
      toast('success', '', 'Payroll processed — salary voucher posted');
      setProcessModal(false);
      load();
    } catch (err) {
      showError('Failed to process payroll', extractError(err));
    }
  };

  const previewRows = previewData && (previewData.data || previewData.payrolls || previewData);
  const previewList = Array.isArray(previewRows) ? previewRows : previewRows?.data || [];

  return (
    <div className="page">
      <PageHeader
        title="Payroll"
        subtitle={`Payroll for ${params.month} — auto calculation and processing.`}
        crumbs={[{ label: 'HR' }, { label: 'Payroll' }]}
        actions={
          hasPermission('generate-payroll') && (
            <>
              <button className="btn btn-secondary" onClick={preview} disabled={previewLoading}>
                <FiFileText /> Preview
              </button>
              <button className="btn btn-primary" onClick={() => { setErrors({}); setGenerateModal(true); }}>
                <FiZap /> Generate Payroll
              </button>
            </>
          )
        }
      />

      <div className="card">
        <div className="card-body">
          <div className="filters-bar">
            <div className="topbar-search">
              <FiSearch className="search-icon" />
              <input placeholder="Search employee..." value={params.search} onChange={(e) => setParams({ ...params, search: e.target.value, page: 1 })} />
            </div>
            <input className={`form-control ${errors.month ? 'is-invalid' : ''}`} type="month" value={params.month} onChange={(e) => { setParams({ ...params, month: e.target.value, page: 1 }); if (errors.month) setErrors((p) => ({ ...p, month: '' })); }} />
            {errors.month && <div className="invalid-feedback">{errors.month}</div>}
          </div>

          <DataTable
            loading={loading}
            data={rows}
            emptyTitle="No payroll records for this month"
            columns={[
              { key: 'employee', label: 'Employee', render: (r) => (r.employee ? r.employee.name : r.employee_name || '-') },
              { key: 'month', label: 'Month' },
              { key: 'gross_earnings', label: 'Gross', align: 'right', render: (r) => formatMoney(r.gross_earnings) },
              { key: 'special_thread_amount', label: 'Special Thread', align: 'right', render: (r) => formatMoney(r.special_thread_amount) },
              { key: 'total_deductions', label: 'Deductions', align: 'right', render: (r) => formatMoney(r.total_deductions) },
              { key: 'net_salary', label: 'Net Salary', align: 'right', render: (r) => <b>{formatMoney(r.net_salary)}</b> },
              { key: 'status', label: 'Status', render: (r) => <StatusBadge status={r.status} /> },
              { key: 'id', label: '', align: 'right', render: (r) => (
                <div className="actions">
                  <Link to={`/payroll/${r.id}`} className="btn-icon" title="View"><FiEye /></Link>
                  {r.status === 'draft' && hasPermission('approve-payroll') && (
                    <button className="btn-icon success" title="Approve" onClick={() => approve(r)}><FiCheckCircle /></button>
                  )}
                  {r.status === 'approved' && hasPermission('process-payroll') && (
                    <button className="btn-icon" title="Process" onClick={() => openProcess(r)}><FiPlay /></button>
                  )}
                </div>
              ) },
            ]}
          />
          {meta && <Pagination meta={meta} onPage={(p) => setParams({ ...params, page: p })} onPerPage={(n) => setParams({ ...params, per_page: n, page: 1 })} />}
        </div>
      </div>

      <Modal open={previewModal} onClose={() => setPreviewModal(false)} title={`Payroll Preview — ${params.month}`} size="lg"
        footer={<button className="btn btn-secondary" onClick={() => setPreviewModal(false)}>Close</button>}
      >
        {previewLoading ? <div className="loader"><div className="spinner" /></div> : (
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>Employee</th><th>Type</th><th className="table-num">Gross</th>
                  <th className="table-num">Special</th><th className="table-num">Deductions</th><th className="table-num">Net</th>
                </tr>
              </thead>
              <tbody>
                {previewList.map((p) => (
                  <tr key={p.employee_id || p.employee?.id || p.id}>
                    <td>{p.employee ? p.employee.name : p.employee_name}</td>
                    <td>{p.employee_type || p.employee?.employee_type || '-'}</td>
                    <td className="table-num">{formatMoney(p.gross_earnings)}</td>
                    <td className="table-num">{formatMoney(p.special_thread_amount)}</td>
                    <td className="table-num">{formatMoney(p.total_deductions)}</td>
                    <td className="table-num"><b>{formatMoney(p.net_salary)}</b></td>
                  </tr>
                ))}
                {previewList.length === 0 && <tr><td colSpan={6} style={{ textAlign: 'center', color: 'var(--text-muted)' }}>No employees to preview</td></tr>}
              </tbody>
            </table>
          </div>
        )}
      </Modal>

      <Modal open={generateModal} onClose={() => { setErrors({}); setGenerateModal(false); }} title={`Generate Payroll — ${params.month}`}
        footer={<>
          <button className="btn btn-secondary" onClick={() => setGenerateModal(false)}>Cancel</button>
          <button className="btn btn-primary" onClick={generate}>Generate</button>
        </>}
      >
        <p style={{ color: 'var(--text-muted)', fontSize: 13, lineHeight: 1.6 }}>
          This will create draft payroll records for all active employees based on:
          <br />• Operators: stitches × machine rates (or fixed below threshold)
          <br />• Helpers/Staff: fixed basic salary
          <br />• Late deductions: every 3 late days = 1 day deduction
        </p>
      </Modal>

      <Modal open={processModal} onClose={() => setProcessModal(false)} title={`Process Payroll — ${activePayroll?.employee?.name || ''}`}
        footer={<>
          <button className="btn btn-secondary" onClick={() => setProcessModal(false)}>Cancel</button>
          <button className="btn btn-primary" onClick={process}>Process & Post Voucher</button>
        </>}
      >
        <p style={{ color: 'var(--text-muted)', fontSize: 13, lineHeight: 1.6 }}>
          Processing will post the salary voucher to accounts. This action is irreversible.
        </p>
      </Modal>
    </div>
  );
}